import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Numbers() {
  const refs = {
    basics: useRef(null),
    literals: useRef(null),
    arithmetic: useRef(null),
    typeConversion: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const keyPoints = [
    { key: "basics", label: "Number Basics" },
    { key: "literals", label: "Number Literals" },
    { key: "arithmetic", label: "Arithmetic Operations" },
    { key: "typeConversion", label: "Type Conversion" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📘 JavaScript Numbers</h1>

      <p className="tutorial-intro">
        In JavaScript, all numeric values — integers or floating-point — are of
        type <code className="highlight_code">Number</code>. JavaScript uses
        **64-bit floating-point representation** (IEEE 754 standard), which
        means numbers are stored as decimals even if they look like integers.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
        }}
      >
        <h1 style={{ color: "#92400e", fontSize: "2.4rem", margin: 0 }}>
          JavaScript Number Concepts
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click below to explore how JavaScript handles numbers step by step:
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1.25rem",
          }}
        >
          {keyPoints.map((l) => (
            <p key={l.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(l.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {l.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Number Basics */}
      <div ref={refs.basics} className="tutorial-section">
        <h2 className="section-title">1️⃣ Number Basics</h2>
        <p>
          JavaScript treats all numeric values as <b>Number</b> type — there’s
          no separate type for integers or decimals. You can assign numbers with
          or without decimal points.
        </p>
        <ul className="tutorial-list-points">
          <li>Numbers can be positive, negative, or fractional (decimal).</li>
          <li>
            You can perform standard arithmetic operations on them directly.
          </li>
          <li>Large or small numbers are displayed in scientific notation.</li>
        </ul>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let x = 10;
let y = 10.5;
let z = 5e3; // 5000
let w = 5e-3; // 0.005
console.log(x, y, z, w);`}
          />
          <TryItButton
            code={`let x = 10;\nlet y = 10.5;\nlet z = 5e3;\nlet w = 5e-3;\nconsole.log(x, y, z, w);`}
          />
        </div>
        <OutputBox>
          <pre>10 10.5 5000 0.005</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Number Literals */}
      <div ref={refs.literals} className="tutorial-section">
        <h2 className="section-title">2️⃣ Number Literals</h2>
        <p>
          Number literals are written in decimal by default, but JavaScript also
          supports other bases:
        </p>
        <ul className="tutorial-list-points">
          <li>
            <b>Hexadecimal (base 16):</b> starts with <code>0x</code> or{" "}
            <code>0X</code>.
          </li>
          <li>
            <b>Binary (base 2):</b> starts with <code>0b</code> or{" "}
            <code>0B</code>.
          </li>
          <li>
            <b>Octal (base 8):</b> starts with <code>0o</code> or{" "}
            <code>0O</code>.
          </li>
        </ul>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let hex = 0xff; // 255
let binary = 0b1010; // 10
let octal = 0o12; // 10
console.log(hex, binary, octal);`}
          />
          <TryItButton
            code={`let hex=0xff;\nlet binary=0b1010;\nlet octal=0o12;\nconsole.log(hex,binary,octal);`}
          />
        </div>
        <OutputBox>
          <pre>255 10 10</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Arithmetic Operations */}
      <div ref={refs.arithmetic} className="tutorial-section">
        <h2 className="section-title">3️⃣ Arithmetic Operations</h2>
        <p>
          Arithmetic operators are used to perform mathematical calculations on
          numbers.
        </p>
        <ul className="tutorial-list-points">
          <li>
            <b>+</b> → Addition
          </li>
          <li>
            <b>-</b> → Subtraction
          </li>
          <li>
            <b>*</b> → Multiplication
          </li>
          <li>
            <b>/</b> → Division
          </li>
          <li>
            <b>%</b> → Modulus (Remainder)
          </li>
          <li>
            <b>**</b> → Exponentiation (Power)
          </li>
        </ul>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = 10, b = 3;
console.log(a + b); // 13
console.log(a - b); // 7
console.log(a * b); // 30
console.log(a / b); // 3.333...
console.log(a % b); // 1
console.log(a ** b); // 1000`}
          />
          <TryItButton
            code={`let a=10,b=3;\nconsole.log(a+b);\nconsole.log(a-b);\nconsole.log(a*b);\nconsole.log(a/b);\nconsole.log(a%b);\nconsole.log(a**b);`}
          />
        </div>
        <OutputBox>
          <pre>13 7 30 3.333... 1 1000</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ Type Conversion */}
      <div ref={refs.typeConversion} className="tutorial-section">
        <h2 className="section-title">4️⃣ Type Conversion</h2>
        <p>
          JavaScript automatically converts data types when performing certain
          operations, but you can also convert manually:
        </p>
        <ul className="tutorial-list-points">
          <li>
            <b>Number()</b> → Converts any value to a number.
          </li>
          <li>
            <b>parseInt()</b> → Converts a string to an integer.
          </li>
          <li>
            <b>parseFloat()</b> → Converts a string to a floating-point number.
          </li>
          <li>
            <b>toString()</b> → Converts a number to a string.
          </li>
        </ul>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number("5"));       // 5
console.log(parseInt("5.9")); // 5
console.log(parseFloat("5.9")); // 5.9
console.log((10).toString());   // "10"`}
          />
          <TryItButton
            code={`console.log(Number("5"));\nconsole.log(parseInt("5.9"));\nconsole.log(parseFloat("5.9"));\nconsole.log((10).toString());`}
          />
        </div>
        <OutputBox>
          <pre>5 5 5.9 "10"</pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Explore built-in Number methods like toFixed() and toPrecision().
          </li>
          <li>Understand special values like Infinity and NaN.</li>
          <li>Learn how floating-point precision works in JavaScript.</li>
          <li>Practice random number generation using Math.random().</li>
        </ul>
      </div>
    </div>
  );
}
